import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useNotification } from './NotificationContext';
import { apiService } from '../api/apiService';

const TaskPollingContext = createContext();

export const useTaskPolling = () => {
  const context = useContext(TaskPollingContext);
  if (!context) {
    throw new Error('useTaskPolling must be used within TaskPollingProvider');
  }
  return context;
};

export function TaskPollingProvider({ children }) {
  const { updateToast } = useNotification();
  const activeTasks = useRef(new Map()); // Map<taskId, { toastId, projectName, taskName }>
  const pollingRef = useRef(null);

  // Register a task for polling
  const registerTask = useCallback((taskId, toastId, projectName, taskName) => {
    activeTasks.current.set(taskId, { toastId, projectName, taskName });
  }, []);

  // Unregister a task (called when task completes)
  const unregisterTask = useCallback((taskId) => {
    activeTasks.current.delete(taskId);
  }, []);

  // Start global polling
  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      if (activeTasks.current.size === 0) return;

      const taskIds = Array.from(activeTasks.current.keys());

      for (const taskId of taskIds) {
        try {
          const task = await apiService.getTaskStatus(taskId);
          const taskInfo = activeTasks.current.get(taskId);

          if (!taskInfo) continue;

          const { toastId, taskName } = taskInfo;

          // Update toast based on status
          if (task.status === 'completed') {
            updateToast(toastId, {
              status: 'completed',
              type: 'success',
              message: `${taskName.replace('_', ' ')} completed successfully!`,
            });
            // Remove from active tasks after a delay so user can see the success
            setTimeout(() => {
              unregisterTask(taskId);
            }, 5000);
          } else if (task.status === 'failed') {
            updateToast(toastId, {
              status: 'failed',
              type: 'error',
              message: task.error || `${taskName.replace('_', ' ')} failed.`,
            });
            // Keep failed tasks visible until dismissed
            unregisterTask(taskId);
          } else if (task.status === 'in_progress') {
            updateToast(toastId, {
              status: 'running',
              type: 'info',
              message: `Processing ${taskName.replace('_', ' ')}...`,
            });
          }
        } catch (e) {
          console.error('Polling error for task:', e);
        }
      }
    }, 3000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [updateToast, unregisterTask]);

  return (
    <TaskPollingContext.Provider value={{ registerTask }}>
      {children}
    </TaskPollingContext.Provider>
  );
}
